import Cookies from 'js-cookie';

import { translateError, translateErrorResponse } from 'scripts/adapters/gateways/lib/http_response_handler';
import { NetworkError, PermanentError, RequestTimeout } from 'scripts/application/lib/error';
import IdGenerator from 'scripts/domain/contracts/id_generator';
import { HEADER_CORRELATION_ID } from 'scripts/infrastructure/backends/http_client';

// glados sends a heartbeat every 20 seconds, so a turn silent this long has stalled.
export const IDLE_TIMEOUT_MS = 45000;

const RESOURCE = 'team assist v2';
const BASE_PATH = '/sidekick/api/team-assists';
const CSRF_COOKIE = '_csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';
const REQUESTOR_ID_HEADER = 'Gladly-Requestor-Id';

/**
 * glados takes the org and the agent from the session, so the paths carry
 * neither. The HTTP backend can't read a response body as a stream, so a turn
 * goes through `fetch` with the backend's auth (the session cookie, the CSRF
 * header, and the correlation and requestor ids). A turn silent for
 * `IDLE_TIMEOUT_MS` rejects with `RequestTimeout`, and a cancelled one with the
 * `AbortError` its signal raised.
 */
export default class TeamAssistV2Gateway {
  constructor(backend, requestorId, fetchImpl = (...args) => window.fetch(...args)) {
    this.backend = backend;
    this.requestorId = requestorId;
    this.fetchImpl = fetchImpl;
  }

  /**
   * POSTs a turn and calls `onEvent` with each event as it arrives, parsed from
   * its JSON `data` (the event's `type` is inside). Resolves when the stream
   * closes, whether or not a `done` or `error` event came first.
   */
  async streamTurn({ body, onEvent, signal }) {
    const watchdog = new IdleWatchdog(signal, IDLE_TIMEOUT_MS);
    try {
      const response = await this._postTurn(body, watchdog.signal);
      await readEvents(response.body.getReader(), onEvent, watchdog);
    } catch (err) {
      throw watchdog.timedOut && isAbort(err) ? new RequestTimeout(err) : err;
    } finally {
      watchdog.stop();
    }
  }

  /** The agent's active session with this customer: `{ session, messages }`, or `{ session: null, messages: [] }`. `signal` cancels the request. */
  loadActive({ customerId, signal }) {
    return this._request(client => client.get(customerPath(customerId), { signal }));
  }

  /** Ends the agent's active session with this customer; the next turn starts a new one. Resolves `{ cleared }`. */
  clear({ customerId }) {
    return this._request(client => client.delete(customerPath(customerId)));
  }

  async _postTurn(body, signal) {
    let response;
    try {
      response = await this.fetchImpl(BASE_PATH, {
        method: 'POST',
        credentials: 'same-origin',
        headers: this._headers(),
        body: JSON.stringify(body),
        signal,
      });
    } catch (err) {
      throw isAbort(err) ? err : new NetworkError(err);
    }
    if (!response.ok) {
      throw translateGladosErrorResponse({ status: response.status, data: await readJson(response) });
    }
    return response;
  }

  _request(send) {
    return send(this.backend.axios()).then(
      res => res.data,
      err => {
        throw err && err.response ? translateGladosErrorResponse(err.response) : translateError(err);
      }
    );
  }

  _headers() {
    const headers = {
      'Content-Type': 'application/json',
      [HEADER_CORRELATION_ID]: IdGenerator.newId(),
    };
    if (this.requestorId) {
      headers[REQUESTOR_ID_HEADER] = this.requestorId;
    }
    const csrf = Cookies.get(CSRF_COOKIE);
    if (csrf) {
      headers[CSRF_HEADER] = csrf;
    }
    return headers;
  }
}

/**
 * Parses a server-sent event stream fed in arbitrary chunks. `push` returns the
 * events a chunk completed, `flush` any final event the stream ended without
 * terminating. An event ends at a blank line; only its `data:` lines are read,
 * so heartbeat comments (`:`) and `event:` names are skipped, and a `data`
 * payload that isn't JSON is dropped. Accepts `\n`, `\r\n`, and `\r` line ends.
 */
export function createSseEventParser() {
  let buffer = '';

  const drain = () => {
    const events = [];
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const event = parseEvent(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      if (event) {
        events.push(event);
      }
      boundary = buffer.indexOf('\n\n');
    }
    return events;
  };

  return {
    push(chunk) {
      // A `\r` at the end of a chunk may be the first half of a `\r\n`, so it
      // waits for the next chunk before being normalized.
      buffer = (buffer + chunk).replace(/\r\n|\r(?!$)/g, '\n');
      return drain();
    },
    flush() {
      buffer = `${buffer.replace(/\r$/, '\n')}\n\n`;
      const events = drain();
      buffer = '';
      return events;
    },
  };
}

function parseEvent(text) {
  const data = text
    .split('\n')
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice('data:'.length).replace(/^ /, ''));
  if (data.length === 0) {
    return null;
  }
  try {
    return JSON.parse(data.join('\n'));
  } catch (err) {
    return null;
  }
}

async function readEvents(reader, onEvent, watchdog) {
  const decoder = new TextDecoder();
  const parser = createSseEventParser();
  let finished = false;
  try {
    while (!finished) {
      const { done, value } = await readChunk(reader);
      watchdog.reset();
      finished = done;
      // `stream: true` keeps a multi-byte character split across chunks whole.
      parser.push(done ? decoder.decode() : decoder.decode(value, { stream: true })).forEach(onEvent);
    }
    parser.flush().forEach(onEvent);
  } finally {
    if (!finished) {
      reader.cancel().catch(ignoreError);
    }
  }
}

async function readChunk(reader) {
  try {
    return await reader.read();
  } catch (err) {
    throw isAbort(err) ? err : new NetworkError(err);
  }
}

// Aborts the turn when the caller's signal does, or when `reset` isn't called for `timeoutMs`.
class IdleWatchdog {
  constructor(callerSignal, timeoutMs) {
    this.callerSignal = callerSignal;
    this.timeoutMs = timeoutMs;
    this.controller = new AbortController();
    this.signal = this.controller.signal;
    this.timedOut = false;
    this.abort = () => this.controller.abort();
    if (callerSignal && callerSignal.aborted) {
      this.abort();
    } else if (callerSignal) {
      callerSignal.addEventListener('abort', this.abort);
    }
    this.reset();
  }

  reset() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timedOut = true;
      this.abort();
    }, this.timeoutMs);
  }

  stop() {
    clearTimeout(this.timer);
    if (this.callerSignal) {
      this.callerSignal.removeEventListener('abort', this.abort);
    }
  }
}

function ignoreError() {
  return null;
}

// glados answers `{ error, questionId? }` where supernova answers `{ errors: [...] }`,
// and a 403 when the agent may not use Team Assist.
function translateGladosErrorResponse({ status, data }) {
  const body = data || {};
  const errors =
    body.errors || (body.error ? [{ code: body.error, meta: { questionId: body.questionId } }] : undefined);
  if (status === 403) {
    return new PermanentError(errors, RESOURCE);
  }
  return translateErrorResponse({ status, data: { ...body, errors } }, RESOURCE);
}

function customerPath(customerId) {
  return `${BASE_PATH}/customers/${encodeURIComponent(customerId)}`;
}

function isAbort(err) {
  return Boolean(err) && err.name === 'AbortError';
}

async function readJson(response) {
  try {
    return await response.json();
  } catch (err) {
    return null;
  }
}
